import clsx from "clsx";
import React, { forwardRef } from "react";
import { Input as AriaInput, type InputProps as AriaInputProps } from "react-aria-components";
import { type Control, Controller, type FieldPath, type FieldValues } from "react-hook-form";

import { InlineIconButton } from "@/components/ui/buttons/InlineIconButton/InlineIconButton";
import { Loader } from "@/components/ui/status/Loader/Loader";
import { Typography } from "@/components/ui/text/Typography/Typography";

import {
  type TextInputVariantProps,
  TextInputWrapper,
  type TextInputWrapperProps,
  textInput,
} from "./TextInputWrapper";

export type TextInputProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = {
  customRef?: React.ForwardedRef<HTMLInputElement>;
  unit?: string;
  isLoading?: boolean;
  loginLabel?: boolean;
  action?: {
    icon: React.FC<React.SVGProps<SVGSVGElement>>;
    onClick: () => void;
    altText: string;
  };
  formControl?: {
    control: Control<TFieldValues>;
    name: TName;
  };
  hideLabel?: boolean;
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  inputClassName?: string;
} & Omit<TextInputVariantProps, "element"> &
  Omit<TextInputWrapperProps, "children"> &
  AriaInputProps;

const TextInputInner = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  customRef,
  variant,
  unit,
  isLoading,
  action,
  className,
  hideLabel,
  hideLabels,
  icon: Icon,
  inputClassName,
  loginLabel,
  ...props
}: Omit<TextInputProps<TFieldValues, TName>, "formControl">) => {
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <TextInputWrapper
      {...props}
      hideLabels={variant === "inline" || hideLabels}
      hideLabel={hideLabel}
      className={className}
      loginLabel={loginLabel}
    >
      <div
        className="relative"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {Icon && (
          <div className="-translate-y-1/2 absolute top-1/2 left-input-side-spacing flex items-center gap-2">
            <Icon className={clsx("inline-flex h-5 w-5")} />
          </div>
        )}
        <AriaInput
          {...props}
          ref={customRef}
          className={textInput({
            variant,
            className: clsx((isLoading || !!action) && !unit && "pr-11", inputClassName),
          })}
          style={{
            paddingRight: unit
              ? `calc(1.25rem + ${unit.length}ch${isLoading || !!action ? ` + 2rem` : ""})`
              : undefined,
            paddingLeft: Icon ? "2.5rem" : undefined,
          }}
        />

        <div className="-translate-y-1/2 absolute top-1/2 right-input-side-spacing flex items-center gap-2">
          {unit && (
            <Typography
              as="span"
              size="label-2"
              variant="prominent-1"
              className="text-text-default-tertiary"
            >
              {unit}
            </Typography>
          )}

          {isLoading && (
            <div className="inline-flex">
              <Loader />
            </div>
          )}

          {!isLoading && action && isHovered && (
            <InlineIconButton
              icon={action.icon}
              onPress={action.onClick}
              label={action.altText}
              iconClassName="!w-3 !h-3"
            />
          )}
        </div>
      </div>
    </TextInputWrapper>
  );
};

const TextInputWithRef = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
  { formControl, ...props }: Omit<TextInputProps<TFieldValues, TName>, "customRef" | "field">,
  ref: React.ForwardedRef<HTMLInputElement>,
) => {
  if (formControl) {
    return (
      <Controller
        control={formControl.control}
        name={formControl.name}
        render={({ field: { ref: controllerRef, ...field }, fieldState: { error } }) => (
          <TextInputInner
            {...props}
            field={field}
            error={props.error ?? error?.message}
            customRef={controllerRef}
          />
        )}
      />
    );
  }

  return (
    <TextInputInner
      {...props}
      customRef={ref}
    />
  );
};

// When using it with React Hook Form, you should pass `formControl` prop instead of `{...register("name")}`.
// Before: `const { register } = useForm(); <TextInput {...register("email")} />`
// After: `const { control } = useForm(); <TextInput formControl={{ control, name: "email" }} />`
export const TextInput = forwardRef(TextInputWithRef) as <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(
  props: Omit<TextInputProps<TFieldValues, TName>, "customRef" | "field"> & {
    ref?: React.ForwardedRef<HTMLInputElement>;
  },
) => ReturnType<typeof TextInputWithRef>;
